import { FetchResult } from '@apollo/client';
import { singleton } from 'tsyringe';
import { buildApiCacheKey } from './cacheKeyUtils';

type CachedFetchResult = FetchResult<Record<string, any>>;

interface CacheEntry {
  data: CachedFetchResult;
  expiresAt: number;
}

export interface ApiCacheSetOptions {
  ttlMs: number;
  tags?: string[];
}

@singleton()
export class ApiCacheService {
  private entries = new Map<string, CacheEntry>();
  private tagIndex = new Map<string, Set<string>>();

  public buildKey(cacheNamespace: string, operationName: string, variables: Record<string, unknown> = {}): string {
    return buildApiCacheKey(cacheNamespace, operationName, variables);
  }

  public get(key: string): CachedFetchResult | undefined {
    const entry = this.entries.get(key);

    if (!entry) {
      return undefined;
    }

    if (Date.now() >= entry.expiresAt) {
      this.delete(key);
      return undefined;
    }

    return entry.data;
  }

  public set(key: string, data: CachedFetchResult, { ttlMs, tags = [] }: ApiCacheSetOptions): void {
    const now = Date.now();

    // Prevent unbounded growth if expired entries are never read again.
    Array.from(this.entries.entries()).forEach(([existingKey, entry]) => {
      if (now >= entry.expiresAt) {
        this.delete(existingKey);
      }
    });

    this.delete(key);

    this.entries.set(key, {
      data,
      expiresAt: now + ttlMs,
    });

    tags.forEach(tag => {
      const keys = this.tagIndex.get(tag) ?? new Set<string>();
      keys.add(key);
      this.tagIndex.set(tag, keys);
    });
  }

  public invalidateTags(tags: string[]): void {
    const keysToDelete = new Set<string>();

    tags.forEach(tag => {
      const keys = this.tagIndex.get(tag);

      if (!keys) {
        return;
      }

      keys.forEach(key => keysToDelete.add(key));
      this.tagIndex.delete(tag);
    });

    keysToDelete.forEach(key => this.delete(key));
  }

  public clear(): void {
    this.entries.clear();
    this.tagIndex.clear();
  }

  public size(): number {
    return this.entries.size;
  }

  private delete(key: string): void {
    this.entries.delete(key);

    Array.from(this.tagIndex.entries()).forEach(([tag, keys]) => {
      keys.delete(key);

      if (keys.size === 0) {
        this.tagIndex.delete(tag);
      }
    });
  }
}
